import type { ComponentProps, ReactNode } from 'react';
import {
  CalendarDays,
  ClipboardList,
  CreditCard,
  Lock,
  Plus,
  Receipt,
  ScrollText,
  SlidersHorizontal,
  StickyNote,
  Users,
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { DatePicker } from '@/components/molecules/date-picker';
import { FormSectionCard, FormSectionHeader } from '@/components/molecules';
import { RichTextEditor } from '@/components/molecules/rich-text-editor';
import { TagSelector } from '@/components/organisms/TagSelector';
import { EstimateAttachments } from '@/components/organisms/estimates/EstimateAttachments';
import type { EstimateCustomerContentSection } from '@/components/organisms/estimates/EstimateCustomerContentSection';
import { EstimateCustomFieldsSection } from '@/components/organisms/estimates/EstimateCustomFieldsSection';
import { EstimateFinancialsSection } from '@/components/organisms/estimates/EstimateFinancialsSection';
import { DocumentCostPanel } from '@/components/organisms/sales/DocumentCostPanel';
import { DocumentLineItemsCard } from '@/components/organisms/sales/DocumentLineItemsCard';
import { EstimateInternalContentSection } from '@/components/organisms/estimates/EstimateInternalContentSection';
import { EstimateLineItemsSection } from '@/components/organisms/estimates/EstimateLineItemsSection';
import { EstimateSalesAttributionSection } from '@/components/organisms/estimates/EstimateSalesAttributionSection';
import { BusinessUnitOverrideField } from '@/components/organisms/business-units/BusinessUnitOverrideField';
import { JobCustomerContactsSection } from '@/components/organisms/jobs/JobCustomerContactsSection';
import { LeadSelector } from '@/components/organisms/LeadSelector';
import { EstimateScheduleSection } from '@/components/organisms/estimates/EstimateScheduleSection';
import { PaymentScheduleSection } from '@/components/organisms/estimates/PaymentScheduleSection';
import { PaymentScheduleStageList } from '@/components/organisms/payment-schedules/PaymentScheduleStageList';
import type { PublicPaymentPlanStage } from '@/components/public/PaymentPlanPreview';
import { TermsSelector } from '@/components/organisms/TermsSelector';
import { ScopeOfWorkTemplatePicker } from '@/components/organisms/ScopeOfWorkTemplatePicker';
import { EstimateSourceValue } from '@/components/organisms/estimates/EstimateSourceReadout';
import { LEAD_SOURCES, leadSourceLabel } from '@/lib/constants/lead-sources';
import type { EstimateStatus } from '@/types/estimate';
import type { EstimateDocTemplateSummary } from '@/types/estimateDocTemplate';
import type { JobType, SalesAttributionUser } from '@/types/settings';
import { cn } from '@/lib/utils';

type SectionKey =
  | 'details'
  | 'customer'
  | 'lineItems'
  | 'financials'
  | 'salesAttribution'
  | 'schedule'
  | 'forCustomer'
  | 'terms'
  | 'forInternal'
  | 'customFields'
  | 'scope'
  | 'notes'
  | 'paymentSchedule'
  | 'advanced';

type CustomerContentProps = Omit<
  ComponentProps<typeof EstimateCustomerContentSection>,
  'open' | 'onToggle'
>;

/** Fields the quick-fields card renders inline; no standalone details section. */
interface EstimateDetailsProps {
  validUntil: string;
  onValidUntilChange: (value: string) => void;
  tagIds: string[];
  onTagIdsChange: (tagIds: string[]) => void;
}

interface EstimateFormSectionsProps {
  sections: Record<SectionKey, boolean>;
  onToggleSection: (key: SectionKey) => void;
  estimateNumber: string;
  onEstimateNumberChange: (value: string) => void;
  title: string;
  onTitleChange: (value: string) => void;
  jobTypeId: string;
  jobTypeName: string;
  jobTypes: JobType[];
  jobTypesLoading?: boolean;
  jobTypesFailed?: boolean;
  onJobTypeChange: (value: string) => void;
  // Estimate Template Builder — the document template this estimate renders
  // through. Omit `docTemplates` to hide the selector entirely.
  docTemplates?: EstimateDocTemplateSummary[];
  docTemplateId?: string | null;
  onDocTemplateChange?: (value: string | null) => void;
  poNumber: string;
  onPoNumberChange: (value: string) => void;
  commercialIdentityLocked?: boolean;
  isEditMode?: boolean;
  // NOT-542 — Business Unit override control (self-gates on active units +
  // dispatch:reassign; omit to hide entirely).
  businessUnitBlockedReason?: string | null;
  businessUnitProps?: Omit<ComponentProps<typeof BusinessUnitOverrideField>, 'className'>;
  detailsProps: EstimateDetailsProps;
  customerProps: Omit<
    ComponentProps<typeof JobCustomerContactsSection>,
    'open' | 'onToggle' | 'compactWhenSelected' | 'isRecurring'
  >;
  /** Automatic membership recommendation, placed directly under the customer. */
  membershipRecommendation?: ReactNode;
  lineItemsProps: Omit<ComponentProps<typeof EstimateLineItemsSection>, 'open' | 'onToggle'>;
  /** NOT-306: "Recommended add-ons" card rendered below Line items. */
  upsellRecommendations?: ReactNode;
  financialsProps: Omit<ComponentProps<typeof EstimateFinancialsSection>, 'open' | 'onToggle'>;
  /** Internal cost/margin readout beside the lines. Omit to hide it. */
  costPanelProps?: ComponentProps<typeof DocumentCostPanel>;
  salesAttributionProps: Omit<
    ComponentProps<typeof EstimateSalesAttributionSection>,
    'open' | 'onToggle'
  >;
  scheduleProps: Omit<ComponentProps<typeof EstimateScheduleSection>, 'open' | 'onToggle'>;
  customerContentProps: CustomerContentProps;
  termsProps: ComponentProps<typeof TermsSelector>;
  internalContentProps: Omit<
    ComponentProps<typeof EstimateInternalContentSection>,
    'open' | 'onToggle'
  >;
  customFieldsProps?: Omit<ComponentProps<typeof EstimateCustomFieldsSection>, 'open' | 'onToggle'>;
  // NOT-223: estimate-only payment-schedule tile. Omitted for proposals.
  paymentScheduleProps?: {
    isEditMode: boolean;
    estimateId?: string;
    /** Stages of the not-yet-saved plan, so the tile can show what it names. */
    draftStages?: PublicPaymentPlanStage[];
    grandTotal: number;
    estimateStatus?: EstimateStatus;
    depositAmount: number;
    depositRequired: boolean;
    hasTemplate: boolean;
    hasDraftTemplate?: boolean;
    /** Opens the saved editor in edit mode or the local editor in create mode. */
    onSetUpSchedule?: () => void;
  };
}

export function EstimateFormSections({
  sections,
  onToggleSection,
  estimateNumber,
  onEstimateNumberChange,
  title,
  onTitleChange,
  jobTypeId,
  jobTypeName,
  jobTypes,
  jobTypesLoading,
  jobTypesFailed,
  onJobTypeChange,
  docTemplates,
  docTemplateId = null,
  onDocTemplateChange,
  poNumber,
  onPoNumberChange,
  commercialIdentityLocked = false,
  isEditMode = false,
  businessUnitProps,
  businessUnitBlockedReason,
  detailsProps,
  customerProps,
  membershipRecommendation,
  lineItemsProps,
  costPanelProps,
  upsellRecommendations,
  financialsProps,
  salesAttributionProps,
  scheduleProps,
  customerContentProps,
  termsProps,
  internalContentProps,
  customFieldsProps,
  paymentScheduleProps,
}: EstimateFormSectionsProps) {
  // Visibility is independent of values: automatic defaults and saved values
  // must survive closing Advanced without forcing it open.
  const showSchedule = sections.schedule || scheduleProps.bookingType === 'RECURRING';
  const showInternal =
    sections.forInternal ||
    Boolean(internalContentProps.internalNotes.trim()) ||
    internalContentProps.internalAttachments.length > 0;
  const showCustomFields =
    Boolean(customFieldsProps) &&
    (sections.customFields || (customFieldsProps?.activeFieldKeys.length ?? 0) > 0);
  // NOT-223: content-aware auto-expand for the new tiles (so saved estimates
  // open with populated sections expanded).
  const showScope = sections.scope || Boolean(customerContentProps.scopeOfWork?.trim());
  const showNotes =
    sections.notes ||
    Boolean(customerContentProps.notes?.trim()) ||
    customerContentProps.attachments.length > 0;
  const showPaymentSchedule =
    Boolean(paymentScheduleProps) &&
    (sections.paymentSchedule ||
      Boolean(paymentScheduleProps?.hasTemplate) ||
      Boolean(paymentScheduleProps?.hasDraftTemplate));

  return (
    <div className="space-y-3">
      <QuickFieldsCard
        estimateNumber={estimateNumber}
        onEstimateNumberChange={onEstimateNumberChange}
        title={title}
        onTitleChange={onTitleChange}
        jobTypeId={jobTypeId}
        jobTypeName={jobTypeName}
        jobTypes={jobTypes}
        jobTypesLoading={jobTypesLoading}
        jobTypesFailed={jobTypesFailed}
        onJobTypeChange={onJobTypeChange}
        commercialIdentityLocked={commercialIdentityLocked}
        isEditMode={isEditMode}
        businessUnitProps={businessUnitProps}
        detailsProps={detailsProps}
      />

      {businessUnitBlockedReason ? (
        <p className="px-1 text-sm text-muted-foreground" role="status">
          {businessUnitBlockedReason}
        </p>
      ) : (
        <>
          <SectionCard
            icon={<Users className="h-4 w-4" />}
            title="Customer"
            description="Who this estimate is for and where work happens."
          >
            <JobCustomerContactsSection
              open
              bare
              onToggle={() => undefined}
              {...customerProps}
              compactWhenSelected
              showServiceContact={false}
              commercialIdentityLocked={commercialIdentityLocked}
            />
          </SectionCard>

          {membershipRecommendation}
        </>
      )}

      {/* Advanced fields do not depend on a selected business unit. */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 px-1">
          <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Add if needed
          </span>
          <AddChip
            active={sections.advanced}
            icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
            label="Advanced"
            onClick={() => onToggleSection('advanced')}
          />
          {!businessUnitBlockedReason ? (
            <>
              {customerContentProps.onScopeOfWorkChange ? (
                <AddChip
                  active={showScope}
                  icon={<ClipboardList className="h-3.5 w-3.5" />}
                  label="Scope of work"
                  onClick={() => onToggleSection('scope')}
                />
              ) : null}
              <AddChip
                active={showNotes}
                icon={<StickyNote className="h-3.5 w-3.5" />}
                label="Notes to customer"
                onClick={() => onToggleSection('notes')}
              />
              {paymentScheduleProps ? (
                <AddChip
                  active={showPaymentSchedule}
                  icon={<CreditCard className="h-3.5 w-3.5" />}
                  label="Payment schedule"
                  onClick={() => onToggleSection('paymentSchedule')}
                />
              ) : null}
              <AddChip
                active={showSchedule}
                icon={<CalendarDays className="h-3.5 w-3.5" />}
                label="Recurring"
                onClick={() => onToggleSection('schedule')}
              />
              <AddChip
                active={showInternal}
                icon={<Lock className="h-3.5 w-3.5" />}
                label="Internal note"
                onClick={() => onToggleSection('forInternal')}
              />
              {customFieldsProps ? (
                <AddChip
                  active={showCustomFields}
                  icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
                  label="Custom field"
                  onClick={() => onToggleSection('customFields')}
                />
              ) : null}
            </>
          ) : null}
        </div>

        {sections.advanced ? (
          <SituationalPanel title="Advanced" icon={<SlidersHorizontal className="h-4 w-4" />}>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {docTemplates && onDocTemplateChange ? (
                <QuickField label="Document design">
                  <Select
                    value={docTemplateId ?? 'DEFAULT'}
                    onValueChange={(value) =>
                      onDocTemplateChange(value === 'DEFAULT' ? null : value)
                    }
                  >
                    <SelectTrigger className="w-full" aria-label="Document design">
                      <SelectValue placeholder="Default design" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* No explicit design → the tenant's default design is used when
                    document designs are on, otherwise the classic layout;
                    "(built-in)" would wrongly imply the stock look regardless. */}
                      <SelectItem value="DEFAULT">Use the default design</SelectItem>
                      {docTemplateId &&
                      !docTemplates.some((template) => template.id === docTemplateId) ? (
                        <SelectItem value={docTemplateId}>Current design</SelectItem>
                      ) : null}
                      {docTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                          {template.isDefault ? ' (default)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </QuickField>
              ) : null}
              <QuickField label="PO number">
                <Input
                  aria-label="PO number"
                  value={poNumber}
                  maxLength={100}
                  placeholder="Optional"
                  onChange={(event) => onPoNumberChange(event.target.value)}
                />
              </QuickField>
              <QuickField label="Source">
                <EstimateSourceValue source={salesAttributionProps.source} />
              </QuickField>
              <QuickField label="Linked lead">
                <LeadSelector
                  selected={salesAttributionProps.selectedLead}
                  onSelect={salesAttributionProps.onLinkedLeadSelect}
                  onClear={salesAttributionProps.onClearLinkedLead}
                />
              </QuickField>
              <QuickField label="Lead source">
                <Select
                  value={salesAttributionProps.leadSource || 'UNSET'}
                  onValueChange={(value) =>
                    salesAttributionProps.onLeadSourceChange(value === 'UNSET' ? '' : value)
                  }
                >
                  <SelectTrigger className="w-full" aria-label="Lead source">
                    <SelectValue placeholder="Select lead source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UNSET">Not specified</SelectItem>
                    {salesAttributionProps.leadSource &&
                    !LEAD_SOURCES.some(
                      (option) => option.value === salesAttributionProps.leadSource,
                    ) ? (
                      <SelectItem value={salesAttributionProps.leadSource}>
                        {leadSourceLabel(salesAttributionProps.leadSource)}
                      </SelectItem>
                    ) : null}
                    {LEAD_SOURCES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </QuickField>
              <QuickField label="Salesperson">
                <Select
                  value={salesAttributionProps.salespersonId || 'UNSET'}
                  onValueChange={(value) =>
                    salesAttributionProps.onSalespersonChange(value === 'UNSET' ? '' : value)
                  }
                  disabled={
                    commercialIdentityLocked ||
                    (salesAttributionProps.users.length === 0 &&
                      !salesAttributionProps.salespersonId)
                  }
                >
                  <SelectTrigger className="w-full" aria-label="Salesperson">
                    <SelectValue placeholder="Select salesperson" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UNSET">Unassigned</SelectItem>
                    {salesAttributionProps.salespersonId &&
                    !salesAttributionProps.users.some(
                      (user) => user.id === salesAttributionProps.salespersonId,
                    ) ? (
                      <SelectItem value={salesAttributionProps.salespersonId}>
                        {salesAttributionProps.salespersonId}
                      </SelectItem>
                    ) : null}
                    {salesAttributionProps.users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {formatUserName(user)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </QuickField>
            </div>
          </SituationalPanel>
        ) : null}

        {!businessUnitBlockedReason ? (
          <>
            {showScope && customerContentProps.onScopeOfWorkChange ? (
              <SituationalPanel title="Scope of work" icon={<ClipboardList className="h-4 w-4" />}>
                <div className="space-y-1.5">
                  <div className="flex justify-end">
                    <ScopeOfWorkTemplatePicker
                      value={customerContentProps.scopeOfWork ?? ''}
                      onInsert={(value) => customerContentProps.onScopeOfWorkChange?.(value)}
                    />
                  </div>
                  <RichTextEditor
                    value={customerContentProps.scopeOfWork ?? ''}
                    onChange={(value) => customerContentProps.onScopeOfWorkChange?.(value)}
                    placeholder="e.g. Supply and install a 3-ton AC unit on the 2nd floor, including ductwork modifications"
                  />
                </div>
              </SituationalPanel>
            ) : null}

            {showNotes ? (
              <SituationalPanel title="Notes to customer" icon={<StickyNote className="h-4 w-4" />}>
                <EstimateCustomerNotesBlock {...customerContentProps} />
              </SituationalPanel>
            ) : null}

            {showPaymentSchedule && paymentScheduleProps ? (
              <SituationalPanel title="Payment schedule" icon={<CreditCard className="h-4 w-4" />}>
                {paymentScheduleProps.isEditMode && paymentScheduleProps.estimateId ? (
                  <PaymentScheduleSection
                    estimateId={paymentScheduleProps.estimateId}
                    grandTotal={paymentScheduleProps.grandTotal}
                    estimateStatus={paymentScheduleProps.estimateStatus ?? 'DRAFT'}
                    depositAmount={paymentScheduleProps.depositAmount}
                    depositRequired={paymentScheduleProps.depositRequired}
                  />
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {paymentScheduleProps.hasDraftTemplate
                        ? 'A payment schedule is configured and will be created when you save the estimate.'
                        : 'Configure payment stages now. Nothing is created until you save the estimate.'}
                    </p>
                    {/* Naming a schedule without showing it meant reopening the
                        dialog was the only way to see what had been configured. */}
                    <PaymentScheduleStageList stages={paymentScheduleProps.draftStages ?? []} />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={paymentScheduleProps.onSetUpSchedule}
                      disabled={!paymentScheduleProps.onSetUpSchedule}
                    >
                      <Plus className="size-4" />
                      {paymentScheduleProps.hasDraftTemplate
                        ? 'Edit payment schedule'
                        : 'Set up payment schedule'}
                    </Button>
                  </div>
                )}
              </SituationalPanel>
            ) : null}

            {showSchedule ? (
              <SituationalPanel title="Recurring" icon={<CalendarDays className="h-4 w-4" />}>
                <EstimateScheduleSection open bare onToggle={() => undefined} {...scheduleProps} />
              </SituationalPanel>
            ) : null}

            {showInternal ? (
              <SituationalPanel title="Internal note" icon={<Lock className="h-4 w-4" />}>
                <EstimateInternalContentSection
                  open
                  bare
                  onToggle={() => undefined}
                  {...internalContentProps}
                />
              </SituationalPanel>
            ) : null}

            {showCustomFields && customFieldsProps ? (
              <SituationalPanel
                title="Custom fields"
                icon={<SlidersHorizontal className="h-4 w-4" />}
              >
                <EstimateCustomFieldsSection
                  open
                  bare
                  onToggle={() => undefined}
                  {...customFieldsProps}
                />
              </SituationalPanel>
            ) : null}
          </>
        ) : null}
      </div>

      {!businessUnitBlockedReason ? (
        <>
          {/* Shared with the estimate-template editor, which builds the same package
              of work and must therefore offer the same editor. */}
          <DocumentLineItemsCard lineItemsProps={lineItemsProps} costPanelProps={costPanelProps} />

          {upsellRecommendations}

          <SectionCard
            icon={<Receipt className="h-4 w-4" />}
            title="Pricing & total"
            description="Discounts, deposit, tax preview, and totals."
          >
            <EstimateFinancialsSection open bare onToggle={() => undefined} {...financialsProps} />
          </SectionCard>

          <SectionCard
            icon={<ScrollText className="h-4 w-4" />}
            title="Terms & conditions"
            description="Reusable legal and approval terms."
          >
            <TermsSelector {...termsProps} />
          </SectionCard>
        </>
      ) : null}
    </div>
  );
}

function EstimateCustomerNotesBlock({
  notes,
  onNotesChange,
  isEditMode,
  entityType = 'estimate',
  editId,
  pendingAttachmentId,
  attachments,
  onAttachmentsChange,
}: CustomerContentProps) {
  const attachmentField =
    isEditMode && editId ? (
      entityType === 'proposal' ? (
        <EstimateAttachments
          proposalId={editId}
          attachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
        />
      ) : (
        <EstimateAttachments
          estimateId={editId}
          attachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
        />
      )
    ) : entityType === 'proposal' ? (
      <EstimateAttachments
        proposalPendingId={pendingAttachmentId}
        attachments={attachments}
        onAttachmentsChange={onAttachmentsChange}
      />
    ) : (
      <EstimateAttachments
        pendingId={pendingAttachmentId}
        attachments={attachments}
        onAttachmentsChange={onAttachmentsChange}
      />
    );

  return (
    <div className="space-y-4">
      <RichTextEditor
        value={notes}
        onChange={onNotesChange}
        placeholder="Add notes visible to the customer..."
      />
      {attachmentField}
    </div>
  );
}

function QuickFieldsCard({
  estimateNumber,
  onEstimateNumberChange,
  title,
  onTitleChange,
  jobTypeId,
  jobTypeName,
  jobTypes,
  jobTypesLoading,
  jobTypesFailed,
  onJobTypeChange,
  commercialIdentityLocked,
  isEditMode,
  businessUnitProps,
  detailsProps,
}: {
  estimateNumber: string;
  onEstimateNumberChange: (value: string) => void;
  title: string;
  onTitleChange: (value: string) => void;
  jobTypeId: string;
  jobTypeName: string;
  jobTypes: JobType[];
  jobTypesLoading?: boolean;
  jobTypesFailed?: boolean;
  onJobTypeChange: (value: string) => void;
  commercialIdentityLocked: boolean;
  isEditMode: boolean;
  businessUnitProps?: Omit<ComponentProps<typeof BusinessUnitOverrideField>, 'className'>;
  detailsProps: EstimateDetailsProps;
}) {
  return (
    <FormSectionCard className="rounded-lg border bg-card p-3 shadow-none sm:p-4">
      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2 xl:grid-cols-12">
        <div className="md:col-span-2 xl:col-span-6">
          <Label
            htmlFor="estimate-title"
            className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Title
          </Label>
          <Input
            id="estimate-title"
            placeholder="e.g. AC replacement - 2nd floor"
            value={title}
            maxLength={500}
            className="h-10 text-base font-medium"
            onChange={(e) => onTitleChange(e.target.value)}
          />
        </div>

        <QuickField label="Estimate #" className="xl:col-span-2">
          <Input
            id="estimate-number"
            placeholder={isEditMode ? 'Keep current' : 'Auto-generated'}
            value={estimateNumber}
            onChange={(e) => onEstimateNumberChange(e.target.value)}
          />
        </QuickField>

        <QuickField label="Valid until" className="xl:col-span-2">
          <DatePicker
            value={
              detailsProps.validUntil ? new Date(detailsProps.validUntil + 'T00:00:00') : undefined
            }
            formatValue={(date) => format(date, 'MMM d, yyyy')}
            onChange={(date) =>
              detailsProps.onValidUntilChange(date ? format(date, 'yyyy-MM-dd') : '')
            }
          />
        </QuickField>

        {/*
          NOT-584 (A4) — "Approval method" used to live here. It moved to the
          send step: nobody decides signature-vs-click while entering line
          items, they decide it when they decide who they are sending to. The
          value still exists on the estimate (seeded from the tenant's Settings
          default at creation) and is still edited — just in SendConfirmModal.
        */}

        <QuickField label="Job type" className="xl:col-span-2">
          <Select
            value={jobTypeId || 'UNSET'}
            onValueChange={(value) => onJobTypeChange(value === 'UNSET' ? '' : value)}
            disabled={commercialIdentityLocked}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={
                  jobTypesLoading
                    ? 'Loading job types…'
                    : jobTypesFailed
                      ? 'Could not load job types'
                      : 'Select job type'
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="UNSET">Not specified</SelectItem>
              {jobTypeId && !jobTypes.some((jobType) => jobType.id === jobTypeId) ? (
                <SelectItem value={jobTypeId}>{jobTypeName || 'Current job type'}</SelectItem>
              ) : null}
              {jobTypes.map((jobType) => (
                <SelectItem key={jobType.id} value={jobType.id}>
                  {jobType.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </QuickField>

        <QuickField label="Tags" className="md:col-span-2 xl:col-span-12">
          <TagSelector
            scope="estimate"
            value={detailsProps.tagIds}
            onChange={detailsProps.onTagIdsChange}
          />
        </QuickField>

        {/* NOT-542 — Business Unit override. Self-gates on active units +
            dispatch:reassign, so it renders nothing (no grid cell) otherwise. */}
        {businessUnitProps ? (
          <BusinessUnitOverrideField
            {...businessUnitProps}
            className="md:col-span-2 xl:col-span-12"
          />
        ) : null}
      </div>
    </FormSectionCard>
  );
}

function QuickField({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function SectionCard({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <FormSectionCard className="rounded-lg border bg-card p-3 shadow-none sm:p-4">
      <FormSectionHeader
        iconNode={<span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>}
        title={title}
        description={description}
        className="mb-3"
      />
      {children}
    </FormSectionCard>
  );
}

function AddChip({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      className="h-8 rounded-full px-3 text-xs"
      aria-pressed={active}
      onClick={onClick}
    >
      {icon}
      {label}
    </Button>
  );
}

function SituationalPanel({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-3 shadow-none sm:p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function formatUserName(user: SalesAttributionUser): string {
  return (
    user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
  );
}
